/**
 * German subject-area baselines — aggregate labour-market signals for
 * Bachelor's graduates in Germany, published federally per broad field.
 *
 * These are NOT per-course numbers; they're the national baseline used
 * as a Section-8 fallback ONLY when the row has no per-course outcome
 * data (which is every DAAD row — Germany doesn't publish outcomes at
 * that grain federally).
 *
 * Sources cited per entry — figures reflect Bundesagentur für Arbeit
 * "Blickpunkt Akademische Berufe 2024/25" + academics.de salary reports
 * (which republish BA + StepStone data), cross-checked with Destatis
 * "Verdienststrukturerhebung 2024".
 *
 * When updating: keep confidence honest — these are nationwide medians
 * with heavy variance by uni, region and employer size.
 */

export const DE_SUBJECT_BASELINES = {
  "Engineering": {
    median_starting_salary_eur: 46700,
    median_after_5y_eur: 61000,
    unemployment_rate: 0.024,      // 2.4% — MINT shortage field
    source_name: "Bundesagentur für Arbeit + academics.de (2024)",
    source_url: "https://www.academics.de/ratgeber/ingenieur-gehalt",
    notes: "MINT shortage field — Blue Card fast-track applies.",
  },
  "Computer Science & IT": {
    median_starting_salary_eur: 46300,
    median_after_5y_eur: 62000,
    unemployment_rate: 0.028,
    source_name: "Bundesagentur für Arbeit + academics.de (2024)",
    source_url: "https://www.academics.de/ratgeber/informatiker-gehalt",
    notes: "MINT shortage field — Blue Card fast-track applies.",
  },
  "Business & Law": {
    median_starting_salary_eur: 44000,
    median_after_5y_eur: 58000,
    unemployment_rate: 0.034,
    source_name: "Gisma / StepStone Gehaltsreport (2024)",
    source_url: "https://www.gisma.com/blog/average-salary-in-germany-by-industry",
    notes: "Range €40-48k depending on region and firm size.",
  },
  "Natural Sciences": {
    median_starting_salary_eur: 43500,
    median_after_5y_eur: 55000,
    unemployment_rate: 0.032,
    source_name: "Bundesagentur für Arbeit — Blickpunkt Akademische Berufe (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
    notes: "Higher variance — pharma / chemistry industry pays above baseline.",
  },
  "Health & Medicine": {
    median_starting_salary_eur: 49000,
    median_after_5y_eur: 64000,
    unemployment_rate: 0.012,      // near full employment
    source_name: "Bundesagentur für Arbeit + Marburger Bund (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
    notes: "Bachelor-level; medical degrees follow separate Staatsexamen pathway.",
  },
  "Arts & Humanities": {
    median_starting_salary_eur: 36000,
    median_after_5y_eur: 45000,
    unemployment_rate: 0.058,      // highest of all fields
    source_name: "Bundesagentur für Arbeit — Blickpunkt Akademische Berufe (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
    notes: "Wide variance — publishing / media / cultural sector pays below baseline.",
  },
  "Social Sciences": {
    median_starting_salary_eur: 39000,
    median_after_5y_eur: 50000,
    unemployment_rate: 0.041,
    source_name: "Bundesagentur für Arbeit — Blickpunkt Akademische Berufe (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
  },
  "Education": {
    median_starting_salary_eur: 47000,
    median_after_5y_eur: 55000,
    unemployment_rate: 0.014,
    source_name: "Kultusministerkonferenz — Beamtenbesoldung A12/A13 (2024)",
    source_url: "https://www.kmk.org/",
    notes: "Public-service Beamter salaries — starting A12 for grundschule, A13 for Gymnasium.",
  },
  "Agriculture": {
    median_starting_salary_eur: 38000,
    median_after_5y_eur: 48000,
    unemployment_rate: 0.036,
    source_name: "Bundesagentur für Arbeit — Blickpunkt Akademische Berufe (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
  },
  "Other": {
    median_starting_salary_eur: 42000,
    median_after_5y_eur: 52000,
    unemployment_rate: 0.033,
    source_name: "Bundesagentur für Arbeit — all-academic average (2024)",
    source_url: "https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Themen-im-Fokus/Berufe/Akademikerinnen/Allgemeiner-Teil-Nav.html",
    notes: "National academic average across all subjects.",
  },
};

export function getDeBaseline(subject_area) {
  return DE_SUBJECT_BASELINES[subject_area] || DE_SUBJECT_BASELINES["Other"];
}
